import type { GameEvent } from '../schema';

export const HEALTH_EVENTS: GameEvent[] = [
  {
    id: 'health.habitFork',
    title: 'The body keeps a ledger',
    description: 'Nothing is wrong yet. The next twenty years are being decided by what you do most weeks.',
    category: 'health',
    pools: ['pool.health.early', 'pool.health.mid'],
    weight: 8,
    cooldown: 4,
    choices: [
      {
        id: 'commit',
        label: 'Build the habit properly',
        detail: 'Three times a week, whatever else is happening.',
        hint: 'slow',
        tags: ['health', 'discipline'],
        inertiaRisk: 0.35,
        outcomes: [
          {
            weight: 1,
            label: 'It became routine',
            narrative: 'Boring, repetitive, and the highest-leverage thing you did all decade.',
            tone: 'positive',
            effects: [
              { k: 'habit', habit: 'exercise', delta: 20 },
              { k: 'stat', stat: 'health', delta: 8 },
              { k: 'stat', stat: 'energy', delta: 10 },
              { k: 'stat', stat: 'stress', delta: -8 },
              { k: 'aspiration', op: 'adopt', defId: 'goal.fitness' },
            ],
          },
        ],
      },
      {
        id: 'sleep',
        label: 'Fix your sleep instead',
        hint: 'safe',
        tags: ['health'],
        outcomes: [
          {
            weight: 1,
            label: 'Slept properly',
            narrative: 'No gym membership required. Arguably the better trade.',
            tone: 'positive',
            effects: [
              { k: 'habit', habit: 'sleep', delta: 20 },
              { k: 'stat', stat: 'energy', delta: 12 },
              { k: 'stat', stat: 'health', delta: 5 },
              { k: 'stat', stat: 'stress', delta: -7 },
            ],
          },
        ],
      },
      {
        id: 'later',
        label: 'When work calms down',
        hint: 'risky',
        tags: ['avoidance'],
        defers: { id: 'int.health', label: 'Look after your health', topic: 'health', urgency: 30, resumeEventId: 'health.habitFork', decayEventId: 'health.neglectCatchUp' },
        outcomes: [
          {
            weight: 1,
            label: 'Postponed',
            narrative: 'Work does not calm down. That is not a feature of work.',
            tone: 'negative',
            effects: [
              { k: 'habit', habit: 'exercise', delta: -8 },
              { k: 'inertia', op: 'pressure', delta: 6 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'health.neglectCatchUp',
    title: 'The results are back',
    description: 'Blood pressure, weight, something with a number attached. Fifteen years of "later" have arrived.',
    category: 'health',
    pools: ['pool.health.mid', 'pool.health.late'],
    weight: 0,
    automatic: true,
    choices: [
      {
        id: 'ack',
        label: 'Read the letter',
        outcomes: [
          {
            weight: 1,
            label: 'A chronic condition',
            narrative: 'Manageable. Permanent. Entirely predictable in retrospect.',
            tone: 'negative',
            effects: [
              { k: 'health', op: 'condition', id: 'cond.metabolic', label: 'Chronic metabolic condition', severity: 42 },
              { k: 'stat', stat: 'health', delta: -12 },
              { k: 'stat', stat: 'energy', delta: -8 },
              { k: 'regret', op: 'add', id: 'regret.health', label: 'Ignored your health for years', weight: 22 },
              { k: 'memory', title: 'Diagnosed with a chronic condition', tone: 'negative', category: 'health', weight: 8 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'health.symptom',
    title: 'Something is not right',
    description: 'A symptom that has persisted longer than it should. Getting it checked takes an afternoon.',
    category: 'health',
    pools: ['pool.health.mid', 'pool.health.late'],
    weight: 7,
    cooldown: 4,
    requires: [{ k: 'age', min: 28 }],
    weightMods: [
      { conditions: [{ k: 'num', path: 'health', op: '<', value: 58 }], multiplier: 1.8 },
      { conditions: [{ k: 'age', min: 50 }], multiplier: 1.6 },
    ],
    choices: [
      {
        id: 'check',
        label: 'Get it checked',
        hint: 'safe',
        tags: ['health'],
        outcomes: [
          {
            weight: 68,
            label: 'Nothing serious',
            narrative: 'A reassuring twelve minutes and a small bill.',
            tone: 'positive',
            effects: [
              { k: 'cashIncomeMonths', months: -0.2, note: 'medical check' },
              { k: 'stat', stat: 'stress', delta: -5 },
            ],
          },
          {
            weight: 32,
            label: 'Caught early',
            narrative: 'It was something. Because you came in now, it is a treatable something.',
            tone: 'neutral',
            effects: [
              { k: 'cashIncomeMonths', months: -1.6, note: 'treatment' },
              { k: 'health', op: 'condition', id: 'cond.treated', label: 'Treated condition', severity: 18 },
              { k: 'stat', stat: 'health', delta: -5 },
              { k: 'stat', stat: 'stress', delta: 8 },
              { k: 'memory', title: 'Caught it early', tone: 'neutral', category: 'health', weight: 6 },
            ],
          },
        ],
      },
      {
        id: 'ignore',
        label: 'Wait and see',
        hint: 'risky',
        tags: ['avoidance'],
        defers: { id: 'int.medical', label: 'See a doctor', topic: 'health', urgency: 45, decayEventId: 'health.lateDiagnosis' },
        outcomes: [
          {
            weight: 58,
            label: 'It passed',
            narrative: 'It went away on its own, as most things do.',
            tone: 'positive',
            effects: [{ k: 'intention', op: 'resolve', id: 'int.medical' }],
          },
          {
            weight: 42,
            label: 'It got worse',
            narrative: 'It did not go away. It waited.',
            tone: 'negative',
            effects: [
              { k: 'stat', stat: 'health', delta: -8 },
              { k: 'stat', stat: 'stress', delta: 10 },
              { k: 'schedule', afterYears: 3, label: 'The untreated symptom', eventId: 'health.lateDiagnosis', effects: [] },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'health.lateDiagnosis',
    title: 'It should have been caught earlier',
    description: 'The treatment is longer, harder and more expensive than it needed to be.',
    category: 'health',
    pools: ['pool.health.late', 'pool.crisis'],
    weight: 0,
    automatic: true,
    choices: [
      {
        id: 'treat',
        label: 'Begin treatment',
        outcomes: [
          {
            weight: 1,
            label: 'A hard year',
            narrative: 'Months of appointments, unpaid leave, and a body that will not fully return.',
            tone: 'negative',
            effects: [
              { k: 'health', op: 'condition', id: 'cond.serious', label: 'Serious health condition', severity: 58 },
              { k: 'stat', stat: 'health', delta: -20 },
              { k: 'stat', stat: 'energy', delta: -14 },
              { k: 'cashIncomeMonths', months: -5, note: 'treatment and lost income' },
              { k: 'counter', name: 'crises', delta: 1 },
              { k: 'career', op: 'adjust', field: 'performance', delta: -10 },
              { k: 'memory', title: 'A serious illness', tone: 'negative', category: 'health', weight: 10 },
              { k: 'triggerEvent', eventId: 'health.recoveryChoice', afterYears: 1 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'health.acuteEvent',
    title: 'It happened suddenly',
    description: 'An accident, or a body that stopped negotiating. You are not working for a while.',
    category: 'health',
    pools: ['pool.health.mid', 'pool.health.late', 'pool.crisis'],
    weight: 4,
    cooldown: 8,
    requires: [{ k: 'age', min: 30 }],
    weightMods: [
      { conditions: [{ k: 'num', path: 'health', op: '<', value: 50 }], multiplier: 2.2 },
      { conditions: [{ k: 'habit', habit: 'exercise', op: '>=', value: 60 }], multiplier: 0.55 },
      { conditions: [{ k: 'num', path: 'burnoutLoad', op: '>=', value: 65 }], multiplier: 1.7 },
      { conditions: [{ k: 'age', min: 60 }], multiplier: 1.8 },
    ],
    choices: [
      {
        id: 'recover',
        label: 'Focus entirely on recovering',
        hint: 'costly',
        tags: ['health'],
        outcomes: [
          {
            weight: 1,
            label: 'Recovered slowly',
            narrative: 'Six months of doing almost nothing, which turned out to be the only way through.',
            tone: 'negative',
            effects: [
              { k: 'stat', stat: 'health', delta: -18 },
              { k: 'stat', stat: 'energy', delta: -12 },
              { k: 'cashIncomeMonths', months: -4, note: 'medical costs and lost income' },
              { k: 'burnout', op: 'load', delta: -18 },
              { k: 'counter', name: 'crises', delta: 1 },
              { k: 'trait', trait: 'resilience', delta: 8 },
              { k: 'memory', title: 'A serious health event', tone: 'negative', category: 'health', weight: 10 },
              { k: 'schedule', afterYears: 2, label: 'Health returns, partly', effects: [{ k: 'stat', stat: 'health', delta: 12 }, { k: 'stat', stat: 'energy', delta: 8 }] },
            ],
          },
        ],
      },
      {
        id: 'push',
        label: 'Get back to work as fast as possible',
        hint: 'risky',
        tags: ['work', 'denial'],
        outcomes: [
          {
            weight: 1,
            label: 'Returned too soon',
            narrative: 'You were back at your desk in five weeks and never quite right again.',
            tone: 'negative',
            effects: [
              { k: 'stat', stat: 'health', delta: -24 },
              { k: 'health', op: 'condition', id: 'cond.chronicPain', label: 'Chronic pain', severity: 44 },
              { k: 'cashIncomeMonths', months: -1.5, note: 'medical costs' },
              { k: 'career', op: 'adjust', field: 'performance', delta: -6 },
              { k: 'counter', name: 'crises', delta: 1 },
              { k: 'regret', op: 'add', id: 'regret.pushedThrough', label: 'Went back to work too soon', weight: 18 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'health.recoveryChoice',
    title: 'Life after the diagnosis',
    description: 'You have been given a strong incentive to reconsider how you spend your time.',
    category: 'health',
    pools: ['pool.recovery'],
    weight: 0,
    choices: [
      {
        id: 'reorganise',
        label: 'Rebuild your life around health',
        hint: 'costly',
        tags: ['health', 'change'],
        outcomes: [
          {
            weight: 1,
            label: 'Reorganised everything',
            narrative: 'Less work, more walking, and a strange new clarity about what was actually urgent.',
            tone: 'positive',
            effects: [
              { k: 'career', op: 'adjust', field: 'workload', delta: -20 },
              { k: 'salary', mode: 'mult', value: 0.88 },
              { k: 'habit', habit: 'exercise', delta: 22 },
              { k: 'habit', habit: 'sleep', delta: 16 },
              { k: 'habit', habit: 'workaholism', delta: -20 },
              { k: 'stat', stat: 'health', delta: 12 },
              { k: 'stat', stat: 'purpose', delta: 12 },
              { k: 'burnout', op: 'load', delta: -25 },
              { k: 'memory', title: 'Rebuilt life around health', tone: 'milestone', category: 'health', weight: 8 },
            ],
          },
        ],
      },
      {
        id: 'manage',
        label: 'Manage it and carry on',
        hint: 'mixed',
        outcomes: [
          {
            weight: 1,
            label: 'Carried on',
            narrative: 'Medication, appointments, and the same life as before at slightly lower resolution.',
            tone: 'neutral',
            effects: [
              { k: 'fixedExpenses', mode: 'add', value: 3200 },
              { k: 'stat', stat: 'health', delta: 5 },
              { k: 'stat', stat: 'energy', delta: -4 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'health.ageing',
    title: 'The inventory of small failures',
    description: 'Knees, eyes, sleep. Nothing dramatic, everything slightly harder than last decade.',
    category: 'health',
    pools: ['pool.health.old'],
    weight: 8,
    cooldown: 4,
    requires: [{ k: 'age', min: 62 }],
    choices: [
      {
        id: 'stayActive',
        label: 'Keep moving anyway',
        hint: 'safe',
        tags: ['health', 'discipline'],
        outcomes: [
          {
            weight: 1,
            label: 'Stayed active',
            narrative: 'Walking, swimming, and a stubborn refusal to sit down permanently.',
            tone: 'positive',
            effects: [
              { k: 'stat', stat: 'health', delta: 8 },
              { k: 'stat', stat: 'energy', delta: 6 },
              { k: 'habit', habit: 'exercise', delta: 10 },
              { k: 'stat', stat: 'happiness', delta: 6 },
            ],
          },
        ],
      },
      {
        id: 'slowDown',
        label: 'Accept the slowdown',
        hint: 'safe',
        outcomes: [
          {
            weight: 1,
            label: 'Slowed down',
            narrative: 'Comfortable, sedentary, and measurably faster in one direction only.',
            tone: 'neutral',
            effects: [
              { k: 'stat', stat: 'health', delta: -6 },
              { k: 'stat', stat: 'stress', delta: -5 },
              { k: 'habit', habit: 'exercise', delta: -10 },
            ],
          },
        ],
      },
    ],
  },
];
